import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const BOT_NAME = 'Dead Simple Bot';

const responses = {
  greetings: [
    `Hello! I'm the Dead Simple Bot. I can tell you about our IT and accounting services.`,
    ``,
    `Try asking about:`,
    `  - Managed IT Support`,
    `  - Cyber Security / Cyber Essentials`,
    `  - Microsoft 365 Migration`,
    `  - MDR / Endpoint Protection`,
    `  - Anti-Ransomware`,
    `  - Bookkeeping / VAT / PAYE`,
    `  - Tax Returns`,
    `  - Making Tax Digital`,
    `  - Contact details`,
  ].join('\n'),
  'managed it': [
    `MANAGED IT SUPPORT`,
    ``,
    `We take care of your entire IT infrastructure so you don't have to. From servers to desktops, networking to cloud.`,
    ``,
    `  - 24/7 monitoring and support`,
    `  - Proactive maintenance and patching`,
    `  - Help desk for your entire team`,
    `  - Hardware and software lifecycle management`,
    `  - Server and network administration`,
    `  - Vendor management`,
    ``,
    `Call us: 0118 359 2220`,
    `deadsimplecomputing.co.uk`,
  ].join('\n'),
  'cyber security': [
    `CYBER SECURITY SERVICES`,
    ``,
    `Protect your business from threats with our security-first approach:`,
    ``,
    `  - Cyber Essentials certification (required for government contracts)`,
    `  - Penetration testing and vulnerability assessments`,
    `  - Security awareness training for staff`,
    `  - Incident response planning`,
    `  - Dark web monitoring`,
    `  - Email security (SPF, DKIM, DMARC)`,
    `  - Anti-phishing simulations`,
    ``,
    `Important for regulated sectors: FCA, SRA, ICO compliance.`,
    ``,
    `Call us: 0118 359 2220`,
  ].join('\n'),
  'microsoft 365': [
    `MICROSOFT 365 MIGRATION & MANAGEMENT`,
    ``,
    `We are Microsoft 365 specialists:`,
    ``,
    `  - Full migration from on-premises Exchange`,
    `  - SharePoint and Teams deployment`,
    `  - Intune device management and MDM`,
    `  - Conditional Access policies`,
    `  - Microsoft 365 Security (E3/E5)`,
    `  - Data Loss Prevention (DLP) policies`,
    `  - Licence optimisation (stop overpaying)`,
    `  - Ongoing support and staff training`,
    ``,
    `Call us: 0118 359 2220`,
  ].join('\n'),
  'mdr': [
    `MDR & ENDPOINT PROTECTION`,
    ``,
    `Managed Detection and Response - 24/7 threat monitoring:`,
    ``,
    `  - Endpoint Detection & Response (EDR)`,
    `  - Security Operations Centre (SOC) monitoring`,
    `  - Threat hunting and investigation`,
    `  - Automated incident response`,
    `  - Real-time alerting`,
    `  - Monthly security reports`,
    ``,
    `Most businesses don't know they've been breached for 200+ days.`,
    `We find threats in minutes, not months.`,
    ``,
    `Call us: 0118 359 2220`,
  ].join('\n'),
  'ransomware': [
    `ANTI-RANSOMWARE SOLUTIONS`,
    ``,
    `Don't become another statistic:`,
    ``,
    `  - Immutable cloud backups (can't be encrypted by attackers)`,
    `  - Air-gapped backup strategy`,
    `  - Ransomware rollback capability`,
    `  - Email gateway protection`,
    `  - Staff awareness training`,
    `  - Incident response plan and testing`,
    `  - Business continuity planning`,
    ``,
    `Average ransomware payout in the UK: GBP 140,000+`,
    `Average downtime: 23 days.`,
    `Our protection starts from a fraction of that.`,
    ``,
    `Call us: 0118 359 2220`,
  ].join('\n'),
  'bookkeeping': [
    `BOOKKEEPING & ACCOUNTS`,
    ``,
    `Dead Simple Accounting handles your books so you can focus on running your business:`,
    ``,
    `  - Monthly bookkeeping`,
    `  - VAT returns and submissions`,
    `  - Payroll and RTI submissions`,
    `  - PAYE management`,
    `  - Pension auto-enrolment`,
    `  - Management accounts`,
    `  - Cloud accounting (Xero, QuickBooks, Sage)`,
    `  - Bank reconciliations`,
    ``,
    `Call us: 0118 359 3666`,
    `deadsimpleaccounting.co.uk`,
  ].join('\n'),
  'tax': [
    `TAX RETURNS & COMPLIANCE`,
    ``,
    `Deadlines sorted. HMRC handled. Penalties avoided.`,
    ``,
    `  - Self Assessment tax returns`,
    `  - Corporation Tax (CT600)`,
    `  - Capital Gains Tax`,
    `  - R&D Tax Credits`,
    `  - Making Tax Digital (MTD) compliance`,
    `  - CIS returns`,
    `  - P11D / P11D(b) submissions`,
    `  - Tax planning and advisory`,
    ``,
    `Call us: 0118 359 3666`,
  ].join('\n'),
  'mtd': [
    `MAKING TAX DIGITAL (MTD)`,
    ``,
    `MTD is now mandatory. Are you compliant?`,
    ``,
    `  - MTD for VAT (already mandatory)`,
    `  - MTD for Income Tax Self Assessment (coming April 2026)`,
    `  - Quarterly digital submissions to HMRC`,
    `  - Compatible software setup and training`,
    `  - Bridge between spreadsheets and HMRC`,
    ``,
    `Don't wait until HMRC comes knocking.`,
    ``,
    `Call us: 0118 359 3666`,
  ].join('\n'),
  'phone': [
    `CONTACT DETAILS`,
    ``,
    `Dead Simple Computing`,
    `  Phone: 0118 359 2220`,
    `  Email: hello@deadsimplecomputing.co.uk`,
    `  Web:   deadsimplecomputing.co.uk`,
    ``,
    `Dead Simple Accounting`,
    `  Phone: 0118 359 3666`,
    `  Email: hello@deadsimpleaccounting.co.uk`,
    `  Web:   deadsimpleaccounting.co.uk`,
    ``,
    `Based in Reading, Berkshire`,
  ].join('\n'),
  'default': [
    `I'm not sure about that one. Try asking about:`,
    ``,
    `IT Services:`,
    `  - Managed IT / IT Support`,
    `  - Cyber Security / Cyber Essentials`,
    `  - Microsoft 365 / M365 Migration`,
    `  - MDR / Endpoint Protection`,
    `  - Anti-Ransomware / Backup`,
    ``,
    `Accounting:`,
    `  - Bookkeeping / VAT / PAYE`,
    `  - Tax Returns / Corporation Tax`,
    `  - Making Tax Digital`,
    ``,
    `Or ask for our contact details.`,
  ].join('\n'),
};

function getResponse(input) {
  const lower = input.toLowerCase();
  if (lower.match(/\b(hi|hey|hello|sup|yo|hiya|morning|afternoon)\b/)) return responses.greetings;
  if (lower.match(/\b(managed|it support|support|helpdesk|help desk|infrastructure)\b/)) return responses['managed it'];
  if (lower.match(/\b(cyber|security|essentials|hack|phishing|firewall|pen\s?test|awareness)\b/)) return responses['cyber security'];
  if (lower.match(/\b(365|microsoft|m365|teams|sharepoint|migration|intune|conditional)\b/)) return responses['microsoft 365'];
  if (lower.match(/\b(mdr|edr|soc|detection|endpoint|monitoring|threat)\b/)) return responses['mdr'];
  if (lower.match(/\b(ransom|backup|immutable|encrypt|incident|continuity)\b/)) return responses['ransomware'];
  if (lower.match(/\b(book|bookkeeping|accounts|accounting|xero|quickbooks|sage|payroll|paye|vat|pension)\b/)) return responses['bookkeeping'];
  if (lower.match(/\b(tax|hmrc|self.?assessment|corporation|ct600|capital.?gains|r\&d|p11d|cis)\b/)) return responses['tax'];
  if (lower.match(/\b(mtd|making.?tax|digital|quarterly)\b/)) return responses['mtd'];
  if (lower.match(/\b(phone|call|number|contact|email|address|where|ring)\b/)) return responses['phone'];
  return responses['default'];
}

function MSNMessenger({ onClose }) {
  const [view, setView] = useState('contacts'); // 'contacts' or 'chat'
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  function openChat() {
    setView('chat');
    setMessages([{ from: 'bot', text: responses.greetings, time: new Date() }]);
  }

  function sendMessage(e) {
    e.preventDefault();
    if (!input.trim()) return;
    const userMsg = { from: 'user', text: input.trim(), time: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    setTimeout(() => {
      const botReply = { from: 'bot', text: getResponse(userMsg.text), time: new Date() };
      setMessages(prev => [...prev, botReply]);
      setIsTyping(false);
    }, 800 + Math.random() * 1200);
  }

  if (view === 'contacts') {
    return (
      <Div>
        <div className="msn__banner">
          <div className="msn__banner__user">
            <div className="msn__banner__avatar">DS</div>
            <div>
              <div className="msn__banner__name">You</div>
              <div className="msn__banner__status">(Online)</div>
            </div>
          </div>
        </div>
        <div className="msn__contacts">
          <div className="msn__group">
            <div className="msn__group__header">Online (1/1)</div>
            <div className="msn__contact" onDoubleClick={openChat}>
              <div className="msn__contact__icon msn__contact__icon--online" />
              <div className="msn__contact__info">
                <div className="msn__contact__name">{BOT_NAME}</div>
                <div className="msn__contact__msg">Ask me about IT and Accounting services</div>
              </div>
            </div>
          </div>
          <div className="msn__group">
            <div className="msn__group__header">Offline (0)</div>
          </div>
        </div>
        <div className="msn__contacts__footer">
          Double-click a contact to start a conversation
        </div>
      </Div>
    );
  }

  return (
    <Div>
      <div className="msn__chat__header">
        <span className="msn__chat__header__icon msn__contact__icon--online" />
        <span>{BOT_NAME} - Conversation</span>
        <button className="msn__chat__back" onClick={() => setView('contacts')}>Contact List</button>
      </div>
      <div className="msn__chat__bar">
        Dead Simple Computing: 0118 359 2220 | Dead Simple Accounting: 0118 359 3666
      </div>
      <div className="msn__chat__messages">
        {messages.map((msg, i) => (
          <div key={i} className="msn__msg">
            <div className="msn__msg__header">
              <span className={`msn__msg__name msn__msg__name--${msg.from}`}>
                {msg.from === 'bot' ? BOT_NAME : 'You'}
              </span>
              <span className="msn__msg__time">
                ({msg.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})
              </span>
            </div>
            <div className="msn__msg__text">{msg.text}</div>
          </div>
        ))}
        {isTyping && (
          <div className="msn__msg">
            <div className="msn__msg__header">
              <span className="msn__msg__name msn__msg__name--bot">{BOT_NAME}</span>
            </div>
            <div className="msn__msg__typing">is typing a message...</div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <form className="msn__chat__input" onSubmit={sendMessage}>
        <textarea
          className="msn__chat__textarea"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Type a message..."
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(e); } }}
          autoFocus
        />
        <div className="msn__chat__buttons">
          <button type="submit" className="msn__chat__send">Send</button>
        </div>
      </form>
      <div className="msn__statusbar">Online</div>
    </Div>
  );
}

const Div = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  background: #f0f0f0;
  font-family: Tahoma, sans-serif;
  font-size: 11px;

  /* === CONTACT LIST === */
  .msn__banner {
    background: linear-gradient(to right, #2e7d32, #4caf50, #66bb6a);
    padding: 8px 10px;
    color: white;
  }
  .msn__banner__user {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .msn__banner__avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #1b5e20;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
  }
  .msn__banner__name {
    font-weight: bold;
    font-size: 12px;
  }
  .msn__banner__status {
    font-size: 10px;
    opacity: 0.8;
  }
  .msn__contacts {
    flex: 1;
    background: white;
    border: 1px solid #7f9db9;
    margin: 4px;
    overflow-y: auto;
  }
  .msn__group__header {
    background: linear-gradient(to bottom, #f0f0f0, #e0e0e0);
    padding: 3px 8px;
    font-size: 10px;
    font-weight: bold;
    color: #333;
    border-bottom: 1px solid #ccc;
  }
  .msn__contact {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    cursor: pointer;
    &:hover { background: #e8f0ff; }
  }
  .msn__contact__icon {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
    &--online { background: #4caf50; }
    &--offline { background: #999; }
  }
  .msn__contact__name {
    font-size: 11px;
    font-weight: bold;
  }
  .msn__contact__msg {
    font-size: 9px;
    color: #999;
  }
  .msn__contacts__footer {
    padding: 4px 8px;
    font-size: 9px;
    color: #999;
    text-align: center;
    border-top: 1px solid #ddd;
  }

  /* === CHAT VIEW === */
  .msn__chat__header {
    background: linear-gradient(to right, #2e7d32, #4caf50);
    padding: 4px 8px;
    color: white;
    font-weight: bold;
    font-size: 11px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .msn__chat__back {
    margin-left: auto;
    background: rgba(255,255,255,0.2);
    border: 1px solid rgba(255,255,255,0.4);
    color: white;
    font-size: 9px;
    padding: 2px 8px;
    border-radius: 2px;
    cursor: pointer;
    font-family: Tahoma, sans-serif;
    &:hover { background: rgba(255,255,255,0.3); }
  }
  .msn__chat__bar {
    background: linear-gradient(to bottom, #e8e8e8, #d4d4d4);
    padding: 3px 8px;
    font-size: 9px;
    border-bottom: 1px solid #aaa;
    color: #333;
  }
  .msn__chat__messages {
    flex: 1;
    overflow-y: auto;
    background: white;
    border: 1px solid #999;
    margin: 4px;
    padding: 6px;
  }
  .msn__msg {
    margin-bottom: 8px;
  }
  .msn__msg__header {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-bottom: 1px;
  }
  .msn__msg__name {
    font-weight: bold;
    font-size: 11px;
    &--bot { color: #003399; }
    &--user { color: #cc0000; }
  }
  .msn__msg__time {
    font-size: 9px;
    color: #999;
  }
  .msn__msg__text {
    font-size: 11px;
    line-height: 1.4;
    padding-left: 4px;
    white-space: pre-wrap;
    color: #333;
  }
  .msn__msg__typing {
    font-style: italic;
    color: #999;
    font-size: 10px;
    padding-left: 4px;
  }
  .msn__chat__input {
    display: flex;
    margin: 0 4px 4px;
    gap: 4px;
  }
  .msn__chat__textarea {
    flex: 1;
    height: 40px;
    padding: 4px 6px;
    border: 1px solid #7f9db9;
    font-family: Tahoma, sans-serif;
    font-size: 11px;
    resize: none;
    outline: none;
    &:focus { border-color: #3399ff; }
  }
  .msn__chat__buttons {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .msn__chat__send {
    padding: 4px 14px;
    background: linear-gradient(to bottom, #fff, #e3e3e3);
    border: 1px solid #999;
    border-radius: 2px;
    font-family: Tahoma, sans-serif;
    font-size: 10px;
    cursor: pointer;
    height: 100%;
    &:hover { background: linear-gradient(to bottom, #e3e3e3, #fff); }
    &:active { background: #ccc; }
  }
  .msn__statusbar {
    background: linear-gradient(to bottom, #e8e8e8, #d4d4d4);
    padding: 2px 8px;
    font-size: 9px;
    border-top: 1px solid #aaa;
    color: #555;
  }
`;

export default MSNMessenger;
